import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { debounce } from 'lodash';
import { useApi } from '../../hooks/useApi';
import { CompanyResult, CompanySearchResponse } from '../../types/company.types';
import { logger } from '../../utils/logger';

interface CompanySearchProps {
  onCompanySelect: (company: CompanyResult | null) => void;
  error?: string;
  disabled?: boolean;
  value?: CompanyResult | null;
  initialInputValue?: string;
  businessType?: 'Limited Company' | 'Sole Trader' | 'Charity / Trust / Community Org';
}

const formatAddress = (address: CompanyResult['registered_office_address']) => {
  if (!address) return 'Address loading...';
  
  // Build first line with premises (building number) if available
  const firstLineParts = [address.premises, address.address_line_1].filter(Boolean);
  const firstLine = firstLineParts.join(' ');
  
  const parts = [
    firstLine,
    address.address_line_2,
    address.locality,
    address.country,
    address.postal_code,
  ].filter(Boolean);
  
  // If no parts, the address object exists but is empty
  if (parts.length === 0) return 'Address loading...';
  
  return parts.join(', ');
};

const CompanySearch: React.FC<CompanySearchProps> = ({
  onCompanySelect,
  error,
  disabled = false,
  value = null,
  initialInputValue = '',
  businessType,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [options, setOptions] = useState<CompanyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const api = useApi();

  const searchCompanies = useCallback(async (query: string) => {
    if (!query.trim()) {
      setOptions([]);
      return;
    }

    setLoading(true);
    setSearchError(null);

    try {
      logger.debug('Company search started', { query });

      // Build registry search URL with businessType if provided
      const registrySearchUrl = businessType
        ? `/company/registry/search?query=${encodeURIComponent(query)}&businessType=${encodeURIComponent(businessType)}`
        : `/company/registry/search?query=${encodeURIComponent(query)}`;

      // Use Promise.allSettled to run both API calls in parallel
      // This ensures we get results from whichever API responds successfully
      const [registryResult, companiesHouseResult] = await Promise.allSettled([
        api.get<any>(registrySearchUrl),
        api.get<CompanySearchResponse>(`/company/search?query=${encodeURIComponent(query)}`)
      ]);

      logger.debug('Company search API results', {
        registryStatus: registryResult.status,
        companiesHouseStatus: companiesHouseResult.status,
        registryDataLength: registryResult.status === 'fulfilled' ? registryResult.value?.data?.data?.length : 0,
        companiesHouseDataLength: companiesHouseResult.status === 'fulfilled' ? companiesHouseResult.value?.data?.items?.length : 0
      });

      let results: CompanyResult[] = [];

      // Process registry search results (OSCR, Charity Commission, etc.)
      if (registryResult.status === 'fulfilled' && 
          registryResult.value?.data?.success && 
          registryResult.value.data.data && 
          registryResult.value.data.data.length > 0) {
        logger.debug('Processing registry search results', { 
          count: registryResult.value.data.data.length,
          data: registryResult.value.data.data 
        });
        // Convert unified registry results to CompanyResult format
        const registryResults = registryResult.value.data.data.map((result: any) => ({
          title: result.name,
          company_name: result.name,
          company_number: result.registrationNumber,
          company_status: result.status,
          date_of_creation: result.registeredDate,
          registered_office_address: {
            address_line_1: result.registeredAddress?.line1 || '',
            address_line_2: result.registeredAddress?.line2 || '',
            locality: result.registeredAddress?.city || '',
            region: result.registeredAddress?.county || '',
            postal_code: result.registeredAddress?.postcode || '',
            country: result.registeredAddress?.country || 'United Kingdom'
          },
          registry_type: result.registryType
        }));
        results.push(...registryResults);
      }
      
      // Also add Companies House results (if any)
      if (companiesHouseResult.status === 'fulfilled' && companiesHouseResult.value?.data) {
        logger.debug('Processing Companies House results', { count: companiesHouseResult.value.data.items?.length || 0 });
        const chResults = (companiesHouseResult.value.data.items || []).map((item: CompanyResult) => ({
          ...item,
          registry_type: item.registry_type || 'companies_house' as const
        }));
        results.push(...chResults);
      }

      // Remove duplicates based on company number
      const uniqueResults = results.reduce((acc: CompanyResult[], current) => {
        const exists = acc.find(item => item.company_number === current.company_number);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      logger.debug('Setting company search options', { 
        totalResults: results.length,
        uniqueResults: uniqueResults.length,
        results: uniqueResults 
      });
      setOptions(uniqueResults);

      // If both failed, show error
      if (results.length === 0 && 
          registryResult.status === 'rejected' && 
          companiesHouseResult.status === 'rejected') {
        logger.warn('Both company search APIs failed');
        setSearchError('Failed to search companies and charities');
      }
    } catch (err) {
      logger.error('Company search error', { error: err });
      setSearchError('Failed to search companies and charities');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [api, businessType]);

  // Store the latest searchCompanies function in a ref
  const searchCompaniesRef = useRef(searchCompanies);
  useEffect(() => {
    searchCompaniesRef.current = searchCompanies;
  }, [searchCompanies]);

  // Debounce the search to avoid too many API calls
  // Use useMemo without searchCompanies in deps to keep debounce stable
  const debouncedSearch = useMemo(() => {
    return debounce((query: string) => {
      searchCompaniesRef.current(query);
    }, 600);
  }, []); // Empty deps - debounce function never recreated

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    debouncedSearch(newInputValue);
  };

  return (
    <Autocomplete
      id="company-search"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_event, newValue) => {
        onCompanySelect(newValue);
      }}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(options) => {
        // Don't filter options - we already filtered them on the server
        return options;
      }}
      getOptionLabel={(option) => option.company_name || option.title || ''}
      isOptionEqualToValue={(option, value) => option.company_number === value.company_number}
      slotProps={{
        paper: {
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(30, 41, 59, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
            '& .MuiAutocomplete-listbox': {
              '& .MuiAutocomplete-option': {
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              },
            },
            '& .MuiAutocomplete-noOptions': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search Company or Charity"
          placeholder="Enter company/charity name or number"
          error={Boolean(error || searchError)}
          helperText={error || searchError}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiFormHelperText-root': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const registryType = option.registry_type;
        
        const getRegistryBadge = () => {
          if (registryType === 'oscr') {
            return <Chip label="OSCR" size="small" color="success" sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 'bold' }} />;
          } else if (registryType === 'companies_house') {
            return <Chip label="Companies House" size="small" color="primary" sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 'bold' }} />;
          } else if (registryType === 'charity_commission') {
            return <Chip label="Charity Commission" size="small" color="info" sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 'bold' }} />;
          }
          // Default badge if registry_type is missing
          return <Chip label="Unknown" size="small" color="default" sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }} />;
        };

        return (
          <Box component="li" {...props}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {option.company_name || option.title}
                </Typography>
                {getRegistryBadge()}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {option.company_number} | {option.company_status} | 
                Incorporated: {option.date_of_creation ? new Date(option.date_of_creation).toLocaleDateString('en-GB') : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {formatAddress(option.registered_office_address)}
              </Typography>
            </Box>
          </Box>
        );
      }}
      noOptionsText={
        inputValue.trim() === '' 
          ? 'Start typing to search companies and charities'
          : 'No companies or charities found'
      }
    />
  );
};

export default CompanySearch;
